/**
 * Central RBAC access matrix for the frontend.
 *
 * RBAC RULE:
 * UI visibility and navigation are permission-driven and kept in one place so sidebars,
 * route guards, and feature-level access checks stay aligned with the backend permission model.
 */
import { environment } from '../../../environments/environment';
import { PermissionCode, ROLE_PERMISSION_CODES } from './permission-codes';

export interface AccessRequirement {
  anyOf?: readonly PermissionCode[];
  allOf?: readonly PermissionCode[];
  deniedRoles?: readonly string[];
  developmentOnly?: boolean;
}

// RBAC RULE: every screen/module key resolves through this matrix instead of hardcoded role checks.
export const ACCESS_REQUIREMENTS = {
  dashboard: {},
  agencies: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesRead, ROLE_PERMISSION_CODES.agenciesReadOwned]
  },
  agenciesList: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesRead]
  },
  agencyDetail: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesRead, ROLE_PERMISSION_CODES.agenciesReadOwned]
  },
  agencyDirectSales: {
    anyOf: [ROLE_PERMISSION_CODES.inventoryDirectSalesRead]
  },
  agencyCashRead: {
    anyOf: [ROLE_PERMISSION_CODES.inventoryAgencyTransactionsRead]
  },
  agencyCashCreate: {
    anyOf: [ROLE_PERMISSION_CODES.inventoryAgencyTransactionsCreate]
  },
  agenciesCreate: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesCreate]
  },
  agenciesEdit: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesUpdate, ROLE_PERMISSION_CODES.agenciesUpdateOwned]
  },
  supervisors: {
    anyOf: [ROLE_PERMISSION_CODES.usersRead],
    deniedRoles: ['supervisor']
  },
  supervisorsCreate: {
    anyOf: [ROLE_PERMISSION_CODES.usersCreateSupervisor]
  },
  supervisorsEdit: {
    anyOf: [ROLE_PERMISSION_CODES.usersUpdate]
  },
  supervisorsApprovalRequests: {
    anyOf: [ROLE_PERMISSION_CODES.supervisorsApprove, ROLE_PERMISSION_CODES.supervisorsReject]
  },
  agents: {
    anyOf: [ROLE_PERMISSION_CODES.usersRead]
  },
  agentsCreate: {
    anyOf: [ROLE_PERMISSION_CODES.usersCreateAgent]
  },
  agentsEdit: {
    anyOf: [ROLE_PERMISSION_CODES.usersUpdate]
  },
  agentCashRead: {
    anyOf: [ROLE_PERMISSION_CODES.inventoryAgentTransactionsRead]
  },
  agentCashCreate: {
    anyOf: [ROLE_PERMISSION_CODES.inventoryAgentTransactionsCreate]
  },
  agentApprovalRequests: {
    anyOf: [ROLE_PERMISSION_CODES.usersRead, ROLE_PERMISSION_CODES.usersUpdateStatus]
  },
  agentTasks: {
    anyOf: [ROLE_PERMISSION_CODES.tasksRead, ROLE_PERMISSION_CODES.tasksReadOwned]
  },
  agentTargets: {
    anyOf: [ROLE_PERMISSION_CODES.targetsRead, ROLE_PERMISSION_CODES.targetsReadOwned]
  },
  agentTargetCreate: {
    anyOf: [ROLE_PERMISSION_CODES.targetsCreate]
  },
  agentTargetEdit: {
    anyOf: [ROLE_PERMISSION_CODES.targetsUpdate, ROLE_PERMISSION_CODES.targetsUpdateOwned]
  },
  agentTargetDelete: {
    anyOf: [ROLE_PERMISSION_CODES.targetsDelete, ROLE_PERMISSION_CODES.targetsDeleteOwned]
  },
  btlTargets: {
    anyOf: [ROLE_PERMISSION_CODES.targetsRead, ROLE_PERMISSION_CODES.targetsReadOwned]
  },
  btlTargetCreate: {
    anyOf: [ROLE_PERMISSION_CODES.targetsCreate]
  },
  btlTargetEdit: {
    anyOf: [ROLE_PERMISSION_CODES.targetsUpdate, ROLE_PERMISSION_CODES.targetsUpdateOwned]
  },
  btlTargetDelete: {
    anyOf: [ROLE_PERMISSION_CODES.targetsDelete, ROLE_PERMISSION_CODES.targetsDeleteOwned]
  },
  agentTaskCreate: {
    anyOf: [ROLE_PERMISSION_CODES.tasksCreate]
  },
  agentTaskEdit: {
    anyOf: [ROLE_PERMISSION_CODES.tasksUpdate, ROLE_PERMISSION_CODES.tasksUpdateOwned]
  },
  agentTaskReview: {
    anyOf: [ROLE_PERMISSION_CODES.taskApprovalReview]
  },
  pos: {
    anyOf: [ROLE_PERMISSION_CODES.posRead, ROLE_PERMISSION_CODES.posReadOwned]
  },
  posCreate: {
    anyOf: [ROLE_PERMISSION_CODES.posCreate]
  },
  posEdit: {
    anyOf: [ROLE_PERMISSION_CODES.posUpdate, ROLE_PERMISSION_CODES.posUpdateOwned]
  },
  prospects: {
    anyOf: [ROLE_PERMISSION_CODES.prospectsRead, ROLE_PERMISSION_CODES.prospectsReadOwned]
  },
  prospectCreate: {
    anyOf: [ROLE_PERMISSION_CODES.prospectsCreate]
  },
  prospectEdit: {
    anyOf: [ROLE_PERMISSION_CODES.prospectsUpdate, ROLE_PERMISSION_CODES.prospectsUpdateOwned]
  },
  prospectApprove: {
    anyOf: [ROLE_PERMISSION_CODES.prospectsApprove]
  },
  inventory: {
    anyOf: [
      ROLE_PERMISSION_CODES.inventoryCentralsRead,
      ROLE_PERMISSION_CODES.inventoryCentralReferenceRead,
      ROLE_PERMISSION_CODES.inventoryCentralStockRead,
      ROLE_PERMISSION_CODES.inventoryCentralTransactionsRead,
      ROLE_PERMISSION_CODES.inventoryBankingNotesRead,
      ROLE_PERMISSION_CODES.inventoryBankingNotesCreate,
      ROLE_PERMISSION_CODES.inventoryBankingNotesUpdate,
      ROLE_PERMISSION_CODES.inventoryBankingNotesDelete
    ],
    deniedRoles: ['supervisor']
  },
  inventoryAddStock: {
    anyOf: [
      ROLE_PERMISSION_CODES.inventoryCentralTransactionsCreate
    ]
  },
  reports: {
    developmentOnly: true
  },
  alerts: {
    developmentOnly: true
  },
  configurations: {
    anyOf: [
      ROLE_PERMISSION_CODES.inventoryCategoriesRead,
      ROLE_PERMISSION_CODES.inventoryProductsRead,
      ROLE_PERMISSION_CODES.inventoryVariantsRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceTypesRead,
      ROLE_PERMISSION_CODES.agenciesRead
    ]
  },
  roles: {
    anyOf: [ROLE_PERMISSION_CODES.rolesRead]
  },
  rolesCreate: {
    anyOf: [ROLE_PERMISSION_CODES.rolesCreate, ROLE_PERMISSION_CODES.rolesUpdate]
  },
  rolesUsersCreate: {
    anyOf: [
      ROLE_PERMISSION_CODES.usersCreateAdmin,
      ROLE_PERMISSION_CODES.usersCreateSupervisor,
      ROLE_PERMISSION_CODES.usersCreateAgent,
      ROLE_PERMISSION_CODES.usersCreateBtl,
      ROLE_PERMISSION_CODES.usersCreateLogistics
    ]
  },
  rolesUsersEdit: {
    anyOf: [ROLE_PERMISSION_CODES.usersUpdate]
  },
  profile: {},
  btl: {
    anyOf: [
      ROLE_PERMISSION_CODES.usersRead,
      ROLE_PERMISSION_CODES.btlDashboardRead,
      ROLE_PERMISSION_CODES.btlRead,
      ROLE_PERMISSION_CODES.btlReadOwned,
      ROLE_PERMISSION_CODES.btlSubmissionsRead,
      ROLE_PERMISSION_CODES.btlSubmissionsReview
    ]
  },
  btlUserCreate: {
    anyOf: [ROLE_PERMISSION_CODES.usersCreateBtl]
  },
  btlUserEdit: {
    anyOf: [ROLE_PERMISSION_CODES.usersUpdate]
  },
  btlCampaignCreate: {
    anyOf: [ROLE_PERMISSION_CODES.btlCreate]
  },
  btlCampaignEdit: {
    anyOf: [ROLE_PERMISSION_CODES.btlUpdate, ROLE_PERMISSION_CODES.btlUpdateOwned]
  },
  logistics: {
    anyOf: [
      ROLE_PERMISSION_CODES.usersRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsDashboardRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsTasksRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsTasksApprovalReview,
      ROLE_PERMISSION_CODES.aggregationLogisticsRoutesRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsRoutesApprove,
      ROLE_PERMISSION_CODES.aggregationLogisticsRoutesReject,
      ROLE_PERMISSION_CODES.aggregationLogisticsRoutesChangeRequestsReview,
      ROLE_PERMISSION_CODES.aggregationLogisticsVehiclesRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsFuelRequestsRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsFuelRequestsReview,
      ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceRequestsRead,
      ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceRequestsReview
    ]
  },
  logisticsUserCreate: {
    anyOf: [ROLE_PERMISSION_CODES.usersCreateLogistics]
  },
  logisticsUserEdit: {
    anyOf: [ROLE_PERMISSION_CODES.usersUpdate]
  },
  logisticsTaskCreate: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsTasksCreate]
  },
  logisticsTaskEdit: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsTasksUpdate]
  },
  logisticsVehicleCreate: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsVehiclesCreate]
  },
  logisticsVehicleEdit: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsVehiclesUpdate]
  },
  logisticsFuelCreate: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsFuelRequestsCreate]
  },
  logisticsFuelEdit: {
    anyOf: [
      ROLE_PERMISSION_CODES.aggregationLogisticsFuelRequestsUpdate,
      ROLE_PERMISSION_CODES.aggregationLogisticsFuelRequestsReview
    ]
  },
  logisticsMaintenanceCreate: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceRequestsCreate]
  },
  logisticsMaintenanceEdit: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceRequestsReview]
  },
  componentScreen: {
    developmentOnly: true
  },
  configurationsProducts: {
    anyOf: [
      ROLE_PERMISSION_CODES.inventoryCategoriesRead,
      ROLE_PERMISSION_CODES.inventoryProductsRead,
      ROLE_PERMISSION_CODES.inventoryVariantsRead
    ]
  },
  configurationsMaintenanceTypes: {
    anyOf: [ROLE_PERMISSION_CODES.aggregationLogisticsMaintenanceTypesRead]
  },
  configurationsAgencyProfiles: {
    anyOf: [ROLE_PERMISSION_CODES.agenciesRead, ROLE_PERMISSION_CODES.agenciesReadOwned]
  }
} as const satisfies Record<string, AccessRequirement>;

export type AccessKey = keyof typeof ACCESS_REQUIREMENTS;

/**
 * Evaluates one configured access rule against the current permission set.
 * RBAC RULE: denied roles win first, then allOf, then anyOf, with development-only screens
 * short-circuiting to the environment flag.
 */
export const canSatisfyAccessRequirement = (
  grantedPermissions: Iterable<string> | undefined,
  userRole: string | undefined,
  key: AccessKey
): boolean => {
  const requirement: AccessRequirement = ACCESS_REQUIREMENTS[key];

  if (requirement.developmentOnly) {
    return environment.showDevelopmentScreens;
  }

  if (requirement.deniedRoles?.includes(userRole ?? '')) {
    return false;
  }

  const granted = new Set(grantedPermissions ?? []);

  if (requirement.allOf?.length && requirement.allOf.some((permission) => !granted.has(permission))) {
    return false;
  }

  if (requirement.anyOf?.length) {
    return requirement.anyOf.some((permission) => granted.has(permission));
  }

  return true;
};
